import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Badge,
  Banner,
  Breadcrumbs,
  Button,
  Empty,
  LayerCard,
  Loader,
  Table,
  Text,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { ArrowClockwiseIcon, HouseIcon, ScanSmileyIcon } from '@phosphor-icons/react'
import type { FaceEnrollmentStatus, FaceStatusListResponse } from '@hub-wifi/shared'
import { ApiError } from '../api/client'
import { checkAllFaces, listFaceStatus, retryFaceEnrollment } from '../api/faceStatus'
import { useTranslation } from '../i18n'
import { formatDateTime } from '../lib/format'
import { PageHeader } from '../components/kumo/page-header/page-header'

const BADGE_VARIANT: Record<
  FaceEnrollmentStatus,
  'success' | 'warning' | 'info' | 'error' | 'secondary'
> = {
  enrolled: 'success',
  rejected_quality: 'warning',
  duplicate: 'info',
  failed: 'error',
  pending: 'secondary',
}

/**
 * "Face on the door" — admin-only. Shows what the ControlID reader answered
 * to each coworker's face photo, with per-person retry and a backfill
 * ("Check all") that re-sends every photo.
 */
export function FaceStatusPage() {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()
  const [data, setData] = useState<FaceStatusListResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retrying, setRetrying] = useState<string | null>(null)
  const [checkingAll, setCheckingAll] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const cancelRef = useRef(false)

  const load = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      setData(await listFaceStatus(token))
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [getAccessTokenSilently, t])

  useEffect(() => {
    void load()
  }, [load])

  async function retry(coworkerId: string) {
    setRetrying(coworkerId)
    try {
      const token = await getAccessTokenSilently()
      const entry = await retryFaceEnrollment(token, coworkerId)
      toasts.add({
        variant: entry.status === 'enrolled' ? 'success' : 'error',
        title: t(`faceStatus.retryResult.${entry.status}`),
      })
      await load()
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    } finally {
      setRetrying(null)
    }
  }

  async function checkAll() {
    if (!window.confirm(t('faceStatus.checkAllConfirm'))) return
    setCheckingAll(true)
    cancelRef.current = false
    let done = 0
    try {
      let cursor: number | undefined
      for (;;) {
        const token = await getAccessTokenSilently()
        const res = await checkAllFaces(token, cursor)
        done += res.processed
        setProgress({ done, total: res.total })
        if (res.nextCursor === null) break
        if (cancelRef.current) {
          toasts.add({
            variant: 'default',
            title: t('faceStatus.checkAllCancelled', { count: done }),
          })
          break
        }
        cursor = res.nextCursor
      }
      if (!cancelRef.current) {
        toasts.add({ variant: 'success', title: t('faceStatus.checkAllDone', { count: done }) })
      }
      await load()
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    } finally {
      setCheckingAll(false)
      setProgress(null)
    }
  }

  const summary = data?.summary
  const counts: Array<{ key: string; value: number }> = summary
    ? [
        { key: 'faceStatus.count.enrolled', value: summary.enrolled },
        { key: 'faceStatus.count.rejected', value: summary.rejected },
        { key: 'faceStatus.count.duplicate', value: summary.duplicate },
        { key: 'faceStatus.count.pending', value: summary.pending + summary.failed },
      ]
    : []

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('faceStatus.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('faceStatus.title')}
        description={t('faceStatus.description')}
      >
        {checkingAll && progress && (
          <Text variant="secondary" size="sm">
            {t('faceStatus.checkAllProgress', { done: progress.done, total: progress.total })}
          </Text>
        )}
        {checkingAll && (
          <Button variant="ghost" size="sm" onClick={() => (cancelRef.current = true)}>
            {t('faceStatus.checkAllCancel')}
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          icon={<ArrowClockwiseIcon size={16} />}
          loading={checkingAll}
          disabled={checkingAll || loading}
          onClick={() => void checkAll()}
        >
          {t('faceStatus.checkAll')}
        </Button>
      </PageHeader>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error}
        </Banner>
      )}

      {!loading && !error && data && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-4">
            {counts.map((c) => (
              <LayerCard key={c.key}>
                <LayerCard.Primary>
                  <Text as="p" variant="secondary" size="sm">
                    {t(c.key)}
                  </Text>
                  <Text as="p" variant="heading2" DANGEROUS_className="font-heading">
                    {c.value}
                  </Text>
                </LayerCard.Primary>
              </LayerCard>
            ))}
          </div>

          {data.capped && <Banner variant="alert">{t('faceStatus.capped')}</Banner>}

          {data.entries.length === 0 && (
            <Empty
              icon={<ScanSmileyIcon size={48} className="text-kumo-inactive" />}
              title={t('faceStatus.empty')}
            />
          )}

          {data.entries.length > 0 && (
            <LayerCard>
              <LayerCard.Primary className="p-0">
                <div style={{ overflowX: 'auto', width: '100%' }}>
                  <Table>
                    <Table.Header>
                      <Table.Row>
                        <Table.Head>{t('faceStatus.col.coworker')}</Table.Head>
                        <Table.Head>{t('faceStatus.col.company')}</Table.Head>
                        <Table.Head>{t('faceStatus.col.status')}</Table.Head>
                        <Table.Head>{t('faceStatus.col.reason')}</Table.Head>
                        <Table.Head>{t('faceStatus.col.lastTry')}</Table.Head>
                        <Table.Head>{t('faceStatus.col.actions')}</Table.Head>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {data.entries.map((e) => (
                        <Table.Row key={e.coworkerId}>
                          <Table.Cell>{e.coworkerName}</Table.Cell>
                          <Table.Cell>{e.companyName ?? '—'}</Table.Cell>
                          <Table.Cell>
                            <Badge variant={BADGE_VARIANT[e.status]}>
                              {t(`faceStatus.status.${e.status}`)}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            {e.reasonMessage ?? '—'}
                            {e.status === 'duplicate' &&
                              e.matchDeviceUserId !== null &&
                              e.buildingId &&
                              e.readerId && (
                                <>
                                  {' '}
                                  <Link
                                    className="text-kumo-link"
                                    to={`/buildings/${e.buildingId}/readers/${e.readerId}/device-users`}
                                  >
                                    {t('faceStatus.matchedUser', { id: e.matchDeviceUserId })}
                                  </Link>
                                </>
                              )}
                          </Table.Cell>
                          <Table.Cell>
                            {e.attemptedAt ? formatDateTime(e.attemptedAt) : '—'}
                          </Table.Cell>
                          <Table.Cell>
                            <Button
                              variant="ghost"
                              size="sm"
                              loading={retrying === e.coworkerId}
                              disabled={retrying !== null || checkingAll}
                              onClick={() => void retry(e.coworkerId)}
                            >
                              {t('faceStatus.retry')}
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
              </LayerCard.Primary>
            </LayerCard>
          )}
        </div>
      )}
    </div>
  )
}
